import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { LogOut, ShieldCheck, ClipboardCheck, User, MapPin } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import InstallAppButton from "./InstallAppButton";
import AppQrButton from "./AppQrButton";

const NCC_LOGO = "/ncc-logo.png";

export default function Navbar() {
  const loc = useLocation();
  const nav = useNavigate();
  const { user, logout, can } = useAuth();
  const [pendingCount, setPendingCount] = useState(0);
  const [pendingContractors, setPendingContractors] = useState(0);
  const [pendingPayroll, setPendingPayroll] = useState(0);
  const [pendingPayRoster, setPendingPayRoster] = useState(0);
  // Hide the global navbar when Reports is in fullscreen mode.
  // ReportsPage toggles `document.body.reports-fullscreen-active`; we observe it
  // so the bar disappears edge-to-edge for an immersive report view.
  const [reportsFullscreen, setReportsFullscreen] = useState(
    typeof document !== "undefined" && document.body.classList.contains("reports-fullscreen-active")
  );
  useEffect(() => {
    if (typeof document === "undefined") return;
    const update = () => setReportsFullscreen(document.body.classList.contains("reports-fullscreen-active"));
    update();
    const mo = new MutationObserver(update);
    mo.observe(document.body, { attributes: true, attributeFilter: ["class"] });
    return () => mo.disconnect();
  }, []);

  // Poll pending workmen count for users with approve_workmen permission
  useEffect(() => {
    if (!user || !can?.("approve_workmen")) { setPendingCount(0); return; }
    let cancelled = false;
    const fetchCount = () => {
      api.get("/workmen/pending", { silent: true }).then((r) => { if (!cancelled) setPendingCount((r.data || []).length); }).catch(() => {});
    };
    fetchCount();
    const t = setInterval(fetchCount, 60000);  // refresh every minute
    return () => { cancelled = true; clearInterval(t); };
  }, [user, can]);

  // Poll pending contractor / payroll counts for users with respective approve permission
  useEffect(() => {
    if (!user) { setPendingContractors(0); setPendingPayroll(0); setPendingPayRoster(0); return; }
    let cancelled = false;
    const fetchCounts = async () => {
      try {
        if (can?.("approve_contractors")) {
          const r = await api.get("/contractors", { silent: true, params: { status: "pending", is_payroll: false, include_pending: true } });
          if (!cancelled) setPendingContractors((r.data || []).length);
        }
        if (can?.("approve_payroll")) {
          const r = await api.get("/contractors", { silent: true, params: { status: "pending", is_payroll: true, include_pending: true } });
          if (!cancelled) setPendingPayroll((r.data || []).length);
        }
        if (can?.("approve_payroll_roster")) {
          const r = await api.get("/pay-roster/pending", { silent: true });
          if (!cancelled) setPendingPayRoster((r.data || []).length);
        }
      } catch { /* ignore */ }
    };
    fetchCounts();
    const t = setInterval(fetchCounts, 60000);
    return () => { cancelled = true; clearInterval(t); };
  }, [user, can]);

  // Hide global navbar on the login (front) page and dedicated branded reset screen
  if (loc.pathname === "/" || loc.pathname === "/admin/login" || loc.pathname === "/admin/reset-password" || loc.pathname === "/portal" || loc.pathname === "/workman" || loc.pathname === "/privacy-policy") return null;
  // Hide while Reports is in fullscreen — true edge-to-edge experience.
  if (reportsFullscreen) return null;

  // Status and Directory are admin-only (private contractor PII). Directory is now
  // surfaced under Reports → Directory tab to keep the top nav compact.
  // Strict role-based visibility: each item only renders when the logged-in
  // user holds at least one of the required permissions (super admin sees all).
  const canAny = (...ps) => !!user && (user.is_super || ps.some((p) => (user.permissions || []).includes(p)));
  const REPORTS_PERMS = [
    "view_stats", "report_manpower_registration", "report_contractor_workforce", "report_payroll_workforce",
    "report_new_contractors", "report_new_payroll", "report_ready_mobilize", "report_ncc_sites",
    "report_workforce", "report_funnel", "report_directory", "report_statutory", "workmen_roster_report",
    "approve_contractors", "approve_payroll", "approve_workmen", "approve_payroll_roster", "approve_medical",
  ];
  const navItems = user
    ? [
        ...(canAny("view_stats") ? [{ to: "/admin", label: "Home" }] : []),
        ...(canAny("view_contractors", "view_payroll", "edit_contractors") ? [{ to: "/register", label: "Register" }] : []),
        ...(user.is_super ? [{ to: "/status", label: "Status" }] : []),
        ...(canAny(...REPORTS_PERMS) ? [{ to: "/reports", label: "Reports" }] : []),
      ]
    : [
        { to: "/register", label: "Register" },
      ];

  return (
    <header className="sticky top-0 z-50 border-b-2 border-ncc-gold bg-ncc-navy backdrop-blur-md print:hidden">
      <div className="mx-auto flex max-w-screen-2xl items-center justify-between px-4 py-3 md:px-8">
        <Link to={user ? (user.is_super || (user.permissions || []).includes("view_stats") ? "/admin" : "/reports") : "/"} className="flex items-center" data-testid="nav-logo">
          <div className="flex h-12 items-center rounded-md bg-white px-2 py-1">
            <img src={NCC_LOGO} alt="NCC-SWMS · MANPOWER DATABANK" className="h-full w-auto object-contain" />
          </div>
        </Link>

        <nav className="hidden items-center gap-1 md:flex">
          {navItems.map((it) => {
            const active = loc.pathname === it.to;
            return (
              <Link
                key={it.to}
                to={it.to}
                data-testid={`nav-${it.label.toLowerCase()}`}
                className={`industrial-label rounded-md px-4 py-2 transition-colors ${
                  active ? "bg-ncc-gold text-ncc-navy" : "text-white/85 hover:bg-white/10 hover:text-white"
                }`}
              >
                {it.label}
              </Link>
            );
          })}
        </nav>

        <div className="flex items-center gap-2">
          <InstallAppButton variant="ghost" size="sm" label="Install" className="hidden sm:inline-flex" />
          <AppQrButton variant="ghost" size="sm" label="QR" className="hidden md:inline-flex" />
          {user ? (
            <>
              {/* Approval buttons moved to Reports → Approvals in v1.7.
                  The pending-count polling is still kept below to power
                  the count badges IF we ever want a compact indicator
                  in the future. */}
              {(user.is_super || (user.permissions || []).some((p) => p === "manage_roles" || p === "manage_admins" || p === "manage_masters")) && (
                <Link
                  to="/admin/settings"
                  data-testid="nav-admin-settings"
                  className="inline-flex items-center gap-2 rounded-md border border-white/30 bg-white/10 px-3 py-2 industrial-label text-white transition-all hover:-translate-y-0.5 hover:bg-white/20"
                >
                  Settings
                </Link>
              )}
              <button
                onClick={() => { logout(); nav("/"); }}
                data-testid="nav-logout-btn"
                className="inline-flex items-center gap-2 rounded-md bg-ncc-gold px-3 py-2 industrial-label text-ncc-navy transition-all hover:-translate-y-0.5 hover:bg-ncc-gold2"
              >
                <LogOut className="h-4 w-4" /> Logout
              </button>
            </>
          ) : (
            <Link
              to="/admin/login"
              data-testid="nav-admin-login"
              className="inline-flex items-center gap-2 rounded-md bg-ncc-gold px-4 py-2 industrial-label text-ncc-navy transition-all hover:-translate-y-0.5 hover:bg-ncc-gold2"
            >
              <ShieldCheck className="h-4 w-4" /> Admin Login
            </Link>
          )}
        </div>
      </div>
      <div className="h-1 bg-gradient-to-r from-ncc-navy via-ncc-gold to-ncc-gold2" />
      {user && (
        <div
          className="flex flex-wrap items-center gap-x-5 gap-y-1 border-b border-slate-200 bg-white px-4 py-1.5 md:px-8"
          data-testid="nav-user-info-bar"
        >
          <span className="flex items-center gap-1.5 text-[13px] text-slate-700" data-testid="nav-user-name">
            <User className="h-3.5 w-3.5 text-ncc-navy" />
            <span className="industrial-label text-[9px] text-slate-400">User</span>
            <b className="font-semibold text-slate-900">{user.name || user.email}</b>
          </span>
          <span className="flex items-center gap-1.5 text-[13px] text-slate-700" data-testid="nav-user-role">
            <ShieldCheck className="h-3.5 w-3.5 text-ncc-navy" />
            <span className="industrial-label text-[9px] text-slate-400">Role</span>
            <b className="font-semibold text-slate-900">{user.role_name || (user.is_super ? "Super Admin" : "—")}</b>
          </span>
          <span className="flex min-w-0 items-center gap-1.5 text-[13px] text-slate-700" data-testid="nav-user-sites">
            <MapPin className="h-3.5 w-3.5 shrink-0 text-ncc-navy" />
            <span className="industrial-label text-[9px] text-slate-400">Site</span>
            <b className="truncate font-semibold text-slate-900">
              {user.is_super ? "All Sites" : ((user.sites && user.sites.length) ? user.sites.join(", ") : "All Sites")}
            </b>
          </span>
        </div>
      )}
    </header>
  );
}
